/**
 * SEO-Hilfsfunktionen und Route-Meta-Map.
 *
 * Zentraler Mechanismus: setPageMeta(pathname) setzt document.title
 * und <meta name="description"> für alle Routen.
 * Für dynamische Seiten (Hotspot-Detail, Wallet-Detail) gibt es
 * spezialisierte Hooks (useHotspotSeo, useWalletSeo).
 *
 * FIN-SWEEP (shell-dashboard): Titel/Description kommen aus dem i18n-Namespace
 * "seo" (locales/de|en/seo.json) statt hartkodierter deutscher Strings, damit
 * ein Sprachwechsel den Browser-Tab-Titel auch tatsächlich übersetzt (vorher
 * blieb z.B. "Monatsbericht · HeliumTracker" nach Wechsel auf EN stehen).
 * __root.tsx koppelt setPageMeta zusätzlich an i18n.language, damit der Titel
 * bei laufendem Sprachwechsel (ohne Navigation) neu berechnet wird.
 */
import i18n from '../i18n'

const BRAND = 'HeliumTracker'
const BRAND_SUFFIX = ` · ${BRAND}`

type RouteMeta = {
  title: string
  description: string
}

/**
 * Exakte Pfad-Zuordnung (ohne dynamische Segmente) → i18n-Key im "seo"-Namespace.
 *
 * Story 0.2 (v2-Relaunch, Welle 2): Die Map führt nur noch die KANONISCHEN
 * Pfade. Die v1-Pfade (/contacts, /commissions, /payouts, /premium) sind reine
 * Redirect-Routen — sie rendern nie eine Seite und brauchen daher keinen
 * eigenen Titel (der Eintrag wäre tot und würde beim Umbenennen still
 * veralten). Der Titel wird auf dem Zielpfad gesetzt.
 */
const ROUTE_KEY_MAP: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/activities': 'activities',
  '/wallets': 'wallets',
  '/wallets/watched': 'watchedWallets',
  '/hotspots': 'hotspots',
  '/hotspots/map': 'hotspotsMap',
  '/tags': 'tags',
  '/hosts': 'hosts',
  '/splits': 'splits',
  '/settlements': 'settlements',
  '/earn-rate': 'earnRate',
  '/speedtest': 'speedtest',
  '/notifications': 'notifications',
  '/news': 'news',
  // #0409: Route deaktiviert, siehe src/layouts/nav.ts / _auth.top-cities.disabled.tsx.
  // '/top-cities': 'topCities',
  '/top-spots': 'topSpots',
  '/plans': 'plans',
  '/monthly-report': 'monthlyReport',
  '/payments': 'payments',
  '/developer': 'developer',
  '/developer/api-tokens': 'apiTokens',
  '/developer/webhooks': 'webhooks',
  '/api-docs': 'apiDocs',
  '/account/subscription': 'subscription',
  '/settings': 'settings',
  '/help': 'help',
  '/support': 'support',
  '/about': 'about',
  '/legal/imprint': 'legalImprint',
  '/legal/privacy': 'legalPrivacy',
  '/imprint': 'legalImprint',
  '/privacy': 'legalPrivacy',
  '/sites': 'sites',
  '/people': 'people',
  '/notes': 'notes',
  '/admin/revenue': 'adminRevenue',
  '/admin/users': 'adminUsers',
  '/admin/newsletters': 'adminNewsletters',
}

function metaForKey(key: string): RouteMeta {
  return {
    title: `${i18n.t(`seo:${key}.label`)}${BRAND_SUFFIX}`,
    description: i18n.t(`seo:${key}.description`),
  }
}

/** Gibt das Meta-Objekt für einen Pfad zurück (exakt oder Prefix-Match). */
export function getRouteMeta(pathname: string): RouteMeta {
  // Exakter Treffer
  const exactKey = ROUTE_KEY_MAP[pathname]
  if (exactKey) {
    return metaForKey(exactKey)
  }

  // Prefix-Match für verschachtelte dynamische Pfade, z.B. /commissions/123
  const sorted = Object.keys(ROUTE_KEY_MAP).sort((a, b) => b.length - a.length)
  for (const path of sorted) {
    if (pathname.startsWith(path + '/')) {
      return metaForKey(ROUTE_KEY_MAP[path])
    }
  }

  // Fallback
  return {
    title: BRAND,
    description: i18n.t('seo:default.description'),
  }
}

/** Setzt document.title und die Meta-Description-Tag. */
export function setPageMeta(pathname: string): void {
  const meta = getRouteMeta(pathname)
  document.title = meta.title

  let descTag = document.querySelector<HTMLMetaElement>('meta[name="description"]')
  if (!descTag) {
    descTag = document.createElement('meta')
    descTag.name = 'description'
    document.head.appendChild(descTag)
  }
  descTag.content = meta.description
}

/** Setzt document.title und Meta-Description direkt (für dynamische Seiten). */
export function setDynamicMeta(title: string, description: string): void {
  document.title = title

  let descTag = document.querySelector<HTMLMetaElement>('meta[name="description"]')
  if (!descTag) {
    descTag = document.createElement('meta')
    descTag.name = 'description'
    document.head.appendChild(descTag)
  }
  descTag.content = description
}

// --- Oeffentliche Seiten: Canonical, Open Graph, strukturierte Daten ---
//
// Anlass (09.2026): Die Hotspot-Detailseiten sind wieder oeffentlich. Fuer
// Suchmaschinen reichen Titel und Description dort nicht — ohne Canonical
// zaehlen `/hotspots/12`, `/hotspots/12?tab=rewards` und die Adress-Variante
// als drei Seiten mit identischem Inhalt, und ohne JSON-LD verschenkt man die
// Ortsangabe.
//
// Crawler bekommen ueber den nginx-Vhost zusaetzlich eine serverseitig
// gerenderte Fassung (Seo::HotspotsController im Backend). Beide Wege muessen
// dieselben Werte liefern; unterschiedliche Inhalte je Besucher waeren
// Cloaking.

/** Kennzeichnet Tags, die dieses Modul angelegt hat — nur die raeumt es auch weg. */
const MANAGED_ATTR = 'data-seo-managed'

function upsertHeadTag(selector: string, create: () => HTMLElement): HTMLElement {
  const existing = document.head.querySelector<HTMLElement>(selector)
  if (existing) return existing
  const el = create()
  el.setAttribute(MANAGED_ATTR, 'true')
  document.head.appendChild(el)
  return el
}

function setMetaTag(key: string, content: string | undefined, attr: 'name' | 'property' = 'name'): void {
  const selector = `meta[${attr}="${key}"]`
  if (content === undefined) return
  const el = upsertHeadTag(selector, () => {
    const meta = document.createElement('meta')
    meta.setAttribute(attr, key)
    return meta
  })
  el.setAttribute('content', content)
}

/**
 * Setzt oder entfernt `<meta name="robots" content="noindex">`.
 *
 * Hintergrund: Der nginx-Vhost liefert für JEDEN unbekannten Pfad die
 * SPA-Hülle mit Status 200 (`try_files $uri $uri/ /index.html`) — aus Sicht
 * einer Suchmaschine sind das beliebig viele Dubletten derselben Seite. Ein
 * echtes 404 kann das Frontend nicht senden, aber es kann der Suchmaschine
 * sagen, dass diese Seite nicht in den Index gehört.
 *
 * Gibt eine Aufräumfunktion zurück: ohne sie bliebe das noindex hängen,
 * sobald von der 404-Seite auf eine echte Route navigiert wird.
 */
export function setNoIndex(): () => void {
  setMetaTag('robots', 'noindex, follow')
  return () => {
    document.head.querySelector(`meta[name="robots"][${MANAGED_ATTR}]`)?.remove()
  }
}

export type PublicPageMeta = {
  title: string
  description: string
  /** Absolute URL der kanonischen Fassung dieser Seite. */
  canonical: string
  /** JSON-LD als Objekt. */
  structuredData?: Record<string, unknown>
}

/**
 * Setzt Titel, Description, Canonical, Open Graph und JSON-LD fuer eine
 * oeffentliche Detailseite. Gibt eine Aufraeumfunktion zurueck, die die
 * selbst angelegten Tags wieder entfernt — die statischen aus index.html
 * bleiben unberuehrt.
 */
export function setPublicPageMeta(meta: PublicPageMeta): () => void {
  setDynamicMeta(meta.title, meta.description)

  const canonicalEl = upsertHeadTag('link[rel="canonical"]', () => {
    const link = document.createElement('link')
    link.setAttribute('rel', 'canonical')
    return link
  })
  canonicalEl.setAttribute('href', meta.canonical)

  setMetaTag('og:title', meta.title, 'property')
  setMetaTag('og:description', meta.description, 'property')
  setMetaTag('og:type', 'website', 'property')
  setMetaTag('og:url', meta.canonical, 'property')

  let scriptEl: HTMLScriptElement | undefined
  if (meta.structuredData) {
    scriptEl = document.createElement('script')
    scriptEl.type = 'application/ld+json'
    scriptEl.setAttribute(MANAGED_ATTR, 'true')
    scriptEl.textContent = JSON.stringify(meta.structuredData)
    document.head.appendChild(scriptEl)
  }

  return () => {
    scriptEl?.remove()
    document.head
      .querySelectorAll(`[${MANAGED_ATTR}]`)
      .forEach((el) => el.remove())
  }
}

/**
 * Absolute URL auf der oeffentlichen Hauptdomain. Ueber VITE_PUBLIC_WEB_ORIGIN
 * konfigurierbar, damit Vorschau-Umgebungen nicht auf die Produktivdomain
 * kanonisieren.
 */
export function publicUrl(path: string): string {
  const base =
    (import.meta.env.VITE_PUBLIC_WEB_ORIGIN as string | undefined) ?? window.location.origin
  return `${base.replace(/\/$/, '')}${path}`
}
